import { PencilIcon, PlusIcon, TrashIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/ui/link";
import type { OrgDetail } from "../../types";
import { useAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TabLayout } from "../TabLayout";
import { AddEmailNotificationDialog } from "./AddEmailNotificationDialog";
import { AddResultNotificationDialog } from "./AddResultNotificationDialog";
import { RemoveEmailNotificationDialog } from "./RemoveEmailNotificationDialog";
import { RemoveResultNotificationDialog } from "./RemoveResultNotificationDialog";

export function ClientNotifications({ org }: { org: OrgDetail }) {
  return (
    <div className="flex flex-col gap-6 relative">
      <TabLayout
        title="Email notifications"
        description="Configure email addresses with notification type"
        actions={
          <AddEmailNotificationDialog org={org}>
            <Button variant="secondary">
              <PlusIcon data-slot="icon" />
              Add email
            </Button>
          </AddEmailNotificationDialog>
        }
      >
        <EmailNotificationsTable org={org} />
      </TabLayout>
      <TabLayout
        title="Result notifications"
        description="Test result noficiations when 'prelim_result' is configured on email addresses above"
        actions={
          <AddResultNotificationDialog org={org}>
            <Button variant="secondary">
              <PlusIcon data-slot="icon" />
              Add result
            </Button>
          </AddResultNotificationDialog>
        }
      >
        <ResultNotificationsTable org={org} />
      </TabLayout>
    </div>
  );
}

function EmailNotificationsTable({ org }: { org: OrgDetail }) {
  const { options } = useAdminGridOptions(
    org.emailNotifications,
    [
      { field: "email" },
      {
        field: "notificationTypes",
        cellRenderer: (params) => (
          <div className="flex gap-1 flex-wrap items-center h-full">
            {(params.value || []).map((type: string) => (
              <Badge key={type}>{type}</Badge>
            ))}
          </div>
        ),
      },
      {
        headerName: "Actions",
        type: "rightAligned",
        cellRendererParams: (params) => ({
          FormDataEvent: params.data,
        }),
        cellRenderer: (params) => (
          <div className="flex items-center justify-end gap-1 h-full">
            <AddEmailNotificationDialog org={org} notification={params.data}>
              <Button size="icon-sm" variant="outline">
                <PencilIcon />
              </Button>
            </AddEmailNotificationDialog>
            <RemoveEmailNotificationDialog org={org} email={params.data.email}>
              <Button
                size="icon-sm"
                variant="outline"
                className="text-destructive"
              >
                <TrashIcon />
              </Button>
            </RemoveEmailNotificationDialog>
          </div>
        ),
      },
    ],
    {
      sideBar: undefined,
      domLayout: "autoHeight",
    },
  );
  return <AdminGrid {...options} />;
}

function ResultNotificationsTable({ org }: { org: OrgDetail }) {
  const { options } = useAdminGridOptions(
    org.testResultNotifications,
    [
      {
        field: "testSpec.name",
        headerName: "Name",
        flex: 1,
        minWidth: 200,
        cellRenderer: (params) => (
          <Link
            href={{
              to: "/admin/tests/$id/edit",
              params: { id: String(params.id) },
            }}
          >
            {params.name}
          </Link>
        ),
        cellRendererParams: (params) => ({
          id: params.data.testSpecId,
          name: params.data.testSpec.name,
        }),
      },
      {
        field: "testSpec.code",
        headerName: "Code",
        width: 140,
        maxWidth: 200,
      },
      {
        headerName: "Actions",
        type: "rightAligned",
        cellRendererParams: (params) => ({
          data: params.data,
        }),
        cellRenderer: (params) => (
          <div className="flex items-center justify-end gap-1 h-full">
            <RemoveResultNotificationDialog
              org={org}
              testSpecId={params.data.testSpecId}
            >
              <Button
                size="icon-sm"
                variant="outline"
                className="text-destructive"
              >
                <TrashIcon />
              </Button>
            </RemoveResultNotificationDialog>
          </div>
        ),
      },
    ],
    {
      sideBar: undefined,
      domLayout: "autoHeight",
    },
  );
  return <AdminGrid {...options} />;
}
